import { User } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import {
	fetchAuthenticatedAvatar,
	isGitLabAvatarUrl,
} from "../avatarImageCache";
import { safeHttpUrl } from "../utils";

export const Avatar = ({
	alt = "User avatar",
	size = "md",
	className = "",
	src = "",
}) => {
	const { user } = useAuth() || {};
	const [authenticatedSrc, setAuthenticatedSrc] = useState("");
	const [imageFailed, setImageFailed] = useState(false);
	const sizeClasses = {
		sm: "w-4 h-4",
		md: "w-6 h-6",
		lg: "w-8 h-8",
	};

	const iconSizeClasses = {
		sm: "w-2 h-2",
		md: "w-3 h-3",
		lg: "w-4 h-4",
	};

	// Extract initials from alt text (user name)
	const getInitials = (name) => {
		if (!name || name === "User avatar" || name === "Unknown Author")
			return "?";
		return name
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase())
			.slice(0, 2)
			.join("");
	};

	const initials = getInitials(alt);
	const safeSrc = safeHttpUrl(src);
	const needsAuthenticatedFetch = isGitLabAvatarUrl(safeSrc);
	const imageSrc = needsAuthenticatedFetch ? authenticatedSrc : safeSrc;

	useEffect(() => {
		setImageFailed(false);
		setAuthenticatedSrc("");

		if (!safeSrc || !needsAuthenticatedFetch || !user?.access_token) return;

		let cancelled = false;

		fetchAuthenticatedAvatar(safeSrc, user.access_token)
			.then((objectUrl) => {
				if (!cancelled) setAuthenticatedSrc(objectUrl);
			})
			.catch(() => {
				if (!cancelled) setImageFailed(true);
			});

		return () => {
			cancelled = true;
		};
	}, [safeSrc, needsAuthenticatedFetch, user?.access_token]);

	return (
		<div
			className={`rounded-full flex-shrink-0 ${sizeClasses[size]} ${className} bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex items-center justify-center`}
		>
			{imageSrc && !imageFailed ? (
				<img
					src={imageSrc}
					alt={alt}
					className="h-full w-full rounded-full object-cover"
					loading="lazy"
					referrerPolicy="no-referrer"
					onError={() => setImageFailed(true)}
				/>
			) : initials !== "?" ? (
				<span className="text-[8px] font-medium text-[var(--color-text-primary)]">
					{initials}
				</span>
			) : (
				<User
					className={`${iconSizeClasses[size]} text-[var(--color-text-secondary)]`}
				/>
			)}
		</div>
	);
};
